import React, { Component } from 'react';
import axios from 'axios';
import qs from 'qs';
import debounce from 'lodash/debounce';

// Components
import CategoryItem from './CategoryItem';

// State
import { REACT_APP_API_URL, REACT_APP_SITE_KEY } from '../../../../shared/vars';

class CategorySearch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            q: '',
            error: '',
            canSearch: false,
            categories: [],
            selectedIndex: 0,
            visible: false,
        };

        // Handle click outside of react element
        this.setWrapperRef = this.setWrapperRef.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    handleClickOutside(event) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.setState({
                ...this.state,
                visible: false,
            });
        }
    }

    handleKeyPress(event) {
        if (event.key === 'Enter') {
            event.preventDefault();

            if (this.state.visible) {
                this.handleCategoryClick(this.state.categories[this.state.selectedIndex]);
            } else {
                this.performSearch();
            }
        }
    }

    handleKeyPressDebouncer = debounce(function(value) {
        this.performSearch();
    });

    handleKeyDown(event) {
        if (event.key === 'ArrowDown') {
            this.arrowKeyDown();
        } else if (event.key === 'ArrowUp') {
            this.arrowKeyUp();
        } else if (event.key === 'Escape') {
            this.setState({
                visible: false,
            });
        }
    }

    handleInputChange(event) {
        const { value } = event.target;
        this.setState(
            {
                ...this.state,
                canSearch: true,
                q: value,
            },
            this.handleKeyPressDebouncer(value)
        );
    }

    arrowKeyDown() {
        let { selectedIndex, visible } = this.state;

        // Only move item if list if visible
        if (visible) {
            selectedIndex += this.state.categories.length - 1 > selectedIndex ? 1 : 0;
        }

        this.setState({
            visible: true,
            selectedIndex,
        });
    }

    arrowKeyUp() {
        let { selectedIndex, visible } = this.state;

        // Only move item if list if visible
        if (visible) {
            selectedIndex -= selectedIndex > 0 ? 1 : 0;
        }

        this.setState({
            visible: true,
            selectedIndex,
        });
    }

    performSearch() {
        const { q, canSearch } = this.state;
        const { currentlySelected } = this.props;

        // If nothing has changed in the search term, do nothing
        if (!canSearch) return;

        // If the query string is empty, set empty collection
        if (q === '') {
            this.setState({
                selectedIndex: 0,
                canSearch: false,
                categories: [],
            });
            return;
        }

        const selectionIds = currentlySelected.map((item) => {
            return item.id;
        });

        this.setState({
            selectedIndex: 0,
            canSearch: false,
        });

        // Put querystring data together
        const data = qs.stringify(
            {
                q,
                currentlySelected: selectionIds,
            },
            { arrayFormat: ['brackets'] }
        );

        // Make ajax call
        const AUTH_HEADER = { Authorization: REACT_APP_SITE_KEY };
        return axios
            .get(REACT_APP_API_URL + 'categories?' + data, { headers: AUTH_HEADER })
            .then((response) => {
                if (
                    response.status !== 200 ||
                    (response.data.status && response.data.status !== 200)
                ) {
                    this.setState({
                        isLoading: false,
                        error: response.data.message,
                        categories: [],
                        visible: false,
                    });
                } else {
                    this.setState({
                        isLoading: false,
                        categories: !!response.data.categories
                            ? response.data.categories
                            : [],
                        visible: true,
                    });
                }
            })
            .catch((err) => {
                if (err.response) {
                    this.setState({
                        isLoading: false,
                        error: err.response.data.errors
                            ? err.response.data.errors
                            : err.response.data.message,
                        categories: [],
                        visible: false,
                    });
                } else {
                    this.setState({
                        isLoading: false,
                        error: err.message,
                        categories: [],
                        visible: false,
                    });
                }
            });
    }

    handleCategoryClick(category) {
        let categories = this.state.categories;
        let { currentlySelected } = this.props;

        categories = categories.map((item) => {
            // This isn't the item we're looking for
            if (item.id !== category.id) return item;

            // Set the new selected status
            let selectedStatus = currentlySelected.some((cat) => {
                return item.id === cat.id;
            });
            selectedStatus = !selectedStatus;

            // Set the new category
            const newCategory = { ...item, selected: selectedStatus };

            // Item will be selected
            if (selectedStatus) {
                currentlySelected.push(newCategory);
            }
            // Item will not be selected
            else {
                currentlySelected = currentlySelected.filter((selectItem) => {
                    return selectItem.id !== category.id;
                });
            }

            // Return new category
            return newCategory;
        });

        this.setState({
            ...this.state,
            categories,
            visible: false,
        });

        this.props.updateHandler(currentlySelected);
    }

    render() {
        const { q, categories, visible, selectedIndex } = this.state;
        const { currentlySelected } = this.props;

        return (
            <div className="ajax-search-wrapper">
                <div
                    className={`input-placeholder-wrapper placeholder--search-field ${
                        this.props.disabled ? 'disabled' : ''
                    }`}
                >
                    <span className="placeholder icon-search" />
                    <input
                        type="text"
                        value={q}
                        placeholder="Search categories"
                        onKeyPress={(e) => this.handleKeyPress(e)}
                        onKeyDown={(e) => this.handleKeyDown(e)}
                        onChange={(e) => this.handleInputChange(e)}
                        disabled={this.props.disabled}
                    />
                </div>
                <div className="ajax-search-content categories">
                    {categories.length && visible ? (
                        <div className="search-dialog" ref={this.setWrapperRef}>
                            <div className="tooltip-arrow" />
                            <div className="content">
                                {categories.map((cat, i) => {
                                    return (
                                        <CategoryItem
                                            key={i}
                                            currentlySelected={currentlySelected}
                                            category={cat}
                                            isSelected={i === selectedIndex}
                                            handleCategoryClick={(category) =>
                                                this.handleCategoryClick(category)
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default CategorySearch;



// WEBPACK FOOTER //
// ./src/components/admin/products/category-search/CategorySearch.js